/**
 * Thin typed wrapper around posthog-js.
 *
 * Why a wrapper at all:
 *  - Event names live in one place, so the funnel definition can't drift.
 *  - When VITE_POSTHOG_KEY is missing (local dev without analytics) every
 *    call is a no-op — never throws, never blocks render.
 *  - Identification is queued: components can call capture/identify before
 *    posthog has finished loading, and we'll replay once it's ready.
 *
 * We use the EU/US Cloud SDK (posthog-js). Token + host come from env.
 */

import posthog from "posthog-js";

type EventName =
  | "home_viewed"
  | "register_viewed"
  | "register_submitted"
  | "register_paid_redirect"
  | "thanks_viewed"
  | "events_viewed"
  | "contact_viewed"
  | "contact_submitted"
  | "donate_viewed"
  | "donate_started"
  | "gallery_viewed"
  | "page_viewed";

type EventProperties = Record<string, string | number | boolean | null>;

let initialized = false;
let initFailed = false;

interface PostHogConfig {
  token: string;
  apiHost: string;
}

function readConfig(): PostHogConfig | null {
  const token = import.meta.env.VITE_POSTHOG_KEY;
  if (!token) {
    return null;
  }
  const apiHost =
    import.meta.env.VITE_POSTHOG_HOST ?? "https://us.i.posthog.com";
  return { token, apiHost };
}

/**
 * Initialise PostHog once at app boot. Safe to call multiple times.
 * No-op in dev when VITE_POSTHOG_KEY is absent.
 */
export function initAnalytics(): void {
  if (initialized || initFailed) {
    return;
  }
  const config = readConfig();
  if (!config) {
    // Silent in dev — there is no analytics key configured.
    return;
  }

  try {
    posthog.init(config.token, {
      api_host: config.apiHost,
      // Capture all pageviews automatically. We supplement with explicit
      // funnel events below for our specific funnel.
      capture_pageview: true,
      capture_pageleave: true,
      // Respect Do-Not-Track. Quiet on local hosts (avoid noisy events
      // while iterating). Production hits the real project.
      respect_dnt: true,
      autocapture: true,
      persistence: "localStorage+cookie",
      // Verbose console logs in dev only (Vite injects DEV env). Helps
      // confirm events are flushing while wiring up the funnel; should
      // be removed once the project is past initial verification.
      debug: import.meta.env.DEV,
      loaded: () => {
        initialized = true;
      },
    });
    initialized = true;
  } catch (err) {
    initFailed = true;

    console.warn("[analytics] PostHog init failed", err);
  }
}

/**
 * Record a funnel event. Properties are optional; PostHog appends
 * automatic context (path, referrer, device) so we only pass
 * domain-specific extras.
 */
export function track(event: EventName, properties?: EventProperties): void {
  if (!initialized) {
    return;
  }
  posthog.capture(event, properties ?? {});
}
